// Settings → Communication templates. Edit the wording of the built-in client
// email/text templates (merge fields allowed) or reset one to the original.
import { useEffect, useState } from "react";
import { request } from "../lib/api";

interface Template { key: string; name: string; subject: string | null; body: string; customized?: boolean }

export function MessageTemplatesPanel({ canEdit }: { canEdit: boolean }) {
  const [items, setItems] = useState<Template[]>([]);
  const [open, setOpen] = useState<Template | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    request<{ ok: true; templates: Template[] }>("/api/message-templates")
      .then((r) => setItems(r.templates)).catch((e) => setErr(e?.message || "Could not load templates"));
  }, []);

  async function save() {
    if (!open) return;
    setBusy(true); setErr(null);
    try {
      const r = await request<{ ok: true; templates: Template[] }>("/api/message-templates", {
        method: "PUT", body: { key: open.key, name: open.name, subject: open.subject || null, body: open.body },
      });
      setItems(r.templates); setOpen(null);
    } catch (e: any) { setErr(e?.message || "Could not save"); } finally { setBusy(false); }
  }
  async function reset(t: Template) {
    if (!window.confirm(`Put "${t.name}" back to the original wording?`)) return;
    setBusy(true); setErr(null);
    try {
      const r = await request<{ ok: true; templates: Template[] }>("/api/message-templates", { method: "DELETE", body: { key: t.key, confirm: true } });
      setItems(r.templates); setOpen(null);
    } catch (e: any) { setErr(e?.message || "Could not reset"); } finally { setBusy(false); }
  }

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header"><h2>Communication templates</h2></div>
      <p className="muted small" style={{ marginTop: 0 }}>
        Used on each client's Texts &amp; Email tab and for automatic report-pull alerts. Merge fields:
        {" {{first_name}} {{full_name}} {{company_name}} {{company_phone}} {{portal_link}} {{enroll_link}}"} (enroll link = your MyFreeScoreNow link above).
      </p>
      {err && <p className="error-text">{err}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((t) => open?.key === t.key ? (
          <div key={t.key} className="stacked-form" style={{ border: "1px solid var(--line, #2a2a3a)", borderRadius: 8, padding: 12 }}>
            <label className="form-field"><span>Name</span>
              <input value={open.name} maxLength={80} onChange={(e) => setOpen({ ...open, name: e.target.value })} /></label>
            <label className="form-field"><span>Email subject</span>
              <input value={open.subject || ""} maxLength={200} onChange={(e) => setOpen({ ...open, subject: e.target.value })} /></label>
            <label className="form-field"><span>Message</span>
              <textarea rows={6} value={open.body} maxLength={4000} onChange={(e) => setOpen({ ...open, body: e.target.value })} /></label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="ghost-button" onClick={() => setOpen(null)}>Cancel</button>
              <button className="primary-button" onClick={save} disabled={busy || !open.name.trim() || !open.body.trim()}>Save template</button>
            </div>
          </div>
        ) : (
          <div key={t.key} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <strong style={{ flex: 1 }}>{t.name}{t.customized && <span className="muted small"> · edited</span>}</strong>
            {canEdit && <button className="ghost-button" onClick={() => setOpen({ ...t })}>Edit</button>}
            {canEdit && t.customized && <button className="ghost-button" onClick={() => reset(t)} disabled={busy}>Reset</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
